import * as React from "react";
import { cx } from "../../utils/dom";
import { StyleProps } from "../../utils/types";
import {
  AriaSliderProps,
  AriaSliderThumbProps,
  VisuallyHidden,
  mergeProps,
  useFocusRing,
  useNumberFormatter,
  useSlider,
  useSliderThumb,
} from "react-aria";
import { SliderState, useSliderState } from "react-stately";
import { InputLabel } from "../input-label";
import { Color, SizeOption } from "../../utils/theme";

type SliderProps = {
  name?: string;
  size?: SizeOption;
  color?: Color;
} & StyleProps<
  "wrapper" | "track" | "fill" | "thumb" | "innerThumb" | "label"
> &
  AriaSliderProps;

export function Slider({
  className,
  classNames,
  style,
  styles,
  name,
  size = "sm",
  color,
  orientation = "horizontal",
  ...props
}: SliderProps) {
  const trackRef = React.useRef(null);
  const numberFormatter = useNumberFormatter();
  const state = useSliderState({ ...props, numberFormatter });
  const { trackProps, labelProps } = useSlider(
    { ...props, "aria-label": props["aria-label"] ?? "slider" },
    state,
    trackRef
  );

  const percent = state.getThumbPercent(0);

  return (
    <div
      data-color={color}
      data-size={size}
      style={{ ...style, ...styles?.wrapper }}
      className={cx("p-slider", orientation, className, classNames?.wrapper)}
    >
      {props.label && (
        <InputLabel
          style={styles?.label}
          className={classNames?.label}
          {...labelProps}
        >
          {props.label}
        </InputLabel>
      )}
      <div
        {...trackProps}
        ref={trackRef}
        className={cx("p-slider-track", classNames?.track)}
        style={{ ...styles?.track, ...trackProps.style }}
      >
        <div
          style={{ ...styles?.fill, width: `${percent * 100}%` }}
          className={cx("p-slider-track-fill", classNames?.fill)}
        ></div>
        <Thumb
          styles={{ wrapper: styles?.thumb, inner: styles?.innerThumb }}
          classNames={{
            wrapper: classNames?.thumb,
            inner: classNames?.innerThumb,
          }}
          state={state}
          trackRef={trackRef}
          index={0}
          name={name}
        />
      </div>
    </div>
  );
}

type ThumbProps = StyleProps<"wrapper" | "inner"> &
  AriaSliderThumbProps & {
    trackRef: React.RefObject<HTMLDivElement>;
    state: SliderState;
  };

function Thumb({
  state,
  trackRef,
  index,
  name,
  className,
  classNames,
  styles,
  style,
}: ThumbProps) {
  const inputRef = React.useRef(null);
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
      name,
    },
    state
  );

  const { focusProps, isFocusVisible } = useFocusRing();
  return (
    <div
      {...thumbProps}
      data-dragging={isDragging}
      data-focus-visible={isFocusVisible}
      className={cx("p-slider-thumb", className, classNames?.wrapper)}
      style={{ ...thumbProps.style, ...style, ...styles?.wrapper }}
    >
      <div
        style={styles?.inner}
        className={cx("p-slider-thumb-inner", classNames?.inner)}
      ></div>
      <VisuallyHidden>
        <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
    </div>
  );
}
